/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import { Container, Modal, Grid, Button } from 'semantic-ui-react';
import getImageUrl from '@redturtle/iovisit-theme/components/Blocks/Listing/Slider/getImageUrl';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import {
  SliderWrapper,
  VariationWrapper,
  When,
  ContentTypeIcon,
  Icon,
  ListingSliderImage,
  ListingSliderPreviewImageMore,
} from '@redturtle/iovisit-theme/components';
import { sliceText, useIsMobile } from '@redturtle/iovisit-theme/helpers';

const messages = defineMessages({
  read_more: { id: 'Per approfondire', defaultMessage: 'Per approfondire' },
  nextImage: {
    id: 'next_image',
    defaultMessage: 'Passa ad immagine successiva',
  },
  prevImage: {
    id: 'prev_image',
    defaultMessage: "Torna all'immagine precedente",
  },
  close_modal: {
    id: 'close_modal',
    defaultMessage: 'Chiudi la modale',
  },
  slider_wrapper: {
    id: 'slider_wrapper',
    defaultMessage: 'Area di contenuti rotativi',
  },
});

const Slider = (data) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const [previewImage, setPreviewImage] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [previewImageAlt, setPreviewImageAlt] = useState(null);

  const {
    items,
    size = 'l',
    full_width = false,
    show_item_title = true,
    show_icon = false,
    show_thematic_area = true,
    show_item_description = false,
    show_detail_link = true,
    detail_link_text = intl.formatMessage(messages.read_more),
    show_detail_link_arrow = true,
    square_images = false,
    slidesToShow = 1,
    text_bg_color = 'white',
  } = data;

  const imageSize = slidesToShow > 1 ? 'gallery' : 'leadimage';

  function carouselModal(direction) {
    // check current index
    let checkIndex;
    if (direction === 'left') {
      if (previewIndex === 0) {
        checkIndex = data.items.length - 1;
      } else {
        checkIndex = previewIndex - 1;
      }
    } else {
      if (previewIndex === data.items.length - 1) {
        checkIndex = 0;
      } else {
        checkIndex = previewIndex + 1;
      }
    }
    const item = data.items[checkIndex];

    setPreviewImage(getImageUrl(item, 'leadimage'));
    setPreviewImageAlt(item?.description || item?.title);
    setPreviewIndex(checkIndex);
  }

  const leftButtonRef = React.useRef();
  const rightButtonRef = React.useRef();
  const modalCloseRef = React.useRef();

  const refIsFocused = (ref) => {
    return document.activeElement === ref.current?.ref.current;
  };

  useEffect(() => {
    if (
      previewImage != null &&
      !refIsFocused(leftButtonRef) &&
      !refIsFocused(rightButtonRef)
    ) {
      leftButtonRef.current.focus();
    }
  }, [previewImage]);

  return (
    <VariationWrapper {...data} variation="slider">
      <div
        className={cx(`size-${size}`, {
          'full-width': full_width,
          square_images: square_images,
          [`width-for-${items?.length}`]: slidesToShow,
        })}
        aria-label={intl.formatMessage(messages.slider_wrapper)}
      >
        <SliderWrapper
          {...data}
          slidesToShowOnMobile={square_images ? 2 : 1}
          n_items={items?.length}
        >
          {data.items.map((slide, index) => {
            const previewImageUrl = getImageUrl(slide, 'leadimage');

            return (
              <div className="single-slide-wrapper" key={index}>
                <div
                  className={cx('bg-wrapper', {
                    'preview-image': slidesToShow > 1,
                  })}
                >
                  <ListingSliderImage
                    item={slide}
                    size={imageSize}
                    role="presentation"
                    onClick={(e) => {
                      if (slidesToShow > 1) {
                        setPreviewImage(previewImageUrl);
                        setPreviewIndex(index);
                      }
                    }}
                    onKeyUp={(e) => {
                      if (e.keyCode === 13) {
                        //Enter
                        if (slidesToShow > 1) {
                          setPreviewImage(previewImageUrl);
                          setPreviewIndex(index);
                        }
                      }
                    }}
                    tabindex={`${items.length > 1 ? 0 : -1}`}
                  />

                  {(show_item_title ||
                    show_item_description ||
                    show_detail_link) && (
                    <div className="text-overlay">
                      <Container>
                        <div
                          className={cx(
                            `text-overlay-content transparent-bg-${text_bg_color}`,
                            {
                              'text-full-width': slidesToShow > 1,
                            },
                          )}
                        >
                          <div>
                            <div
                              className={cx('header-slide', {
                                'show-icon': show_icon,
                              })}
                            >
                              {show_thematic_area && slide.thematic_area && (
                                <span className="thematic-area-slide">
                                  {slide.thematic_area}
                                </span>
                              )}
                              {show_icon && (
                                <div className="icon-slide">
                                  <ContentTypeIcon item={slide} />
                                </div>
                              )}
                            </div>

                            {show_item_title && (
                              <div
                                className={cx('title', {
                                  'show-icon': show_icon,
                                })}
                              >
                                {slide.title}
                              </div>
                            )}

                            {(slide.start || slide.end) && (
                              <When
                                start={slide.start}
                                end={slide.end}
                                whole_day={slide.whole_day}
                                open_end={slide.open_end}
                                show_times={false}
                                className="dates"
                              />
                            )}

                            {show_item_description && slide.description && (
                              <div className="description">
                                {sliceText(
                                  slide.description,
                                  size,
                                  isMobile,
                                  slidesToShow,
                                )}
                              </div>
                            )}
                          </div>

                          {show_detail_link && (
                            <div
                              className={cx('link', {
                                'show-arrow': show_detail_link_arrow,
                              })}
                            >
                              <UniversalLink item={slide}>
                                {detail_link_text}
                              </UniversalLink>
                            </div>
                          )}
                        </div>
                      </Container>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </SliderWrapper>

        <Modal
          id="modalGallery"
          closeIcon={
            <button
              ref={modalCloseRef}
              className="close"
              title={intl.formatMessage(messages.close_modal)}
            >
              <Icon prefix="fa" icon="times" className="close icon" />
            </button>
          }
          closeOnEscape={true}
          onClose={() => {
            setPreviewImage(null);
            setPreviewIndex(null);
          }}
          open={previewImage != null}
          onKeyUp={(e) => {
            if (e.keyCode === 37) {
              //arrow-left
              carouselModal('left');
            }
            if (e.keyCode === 39) {
              //arrow-right
              carouselModal('right');
            }
          }}
          onKeyDown={(e) => {
            if (e.keyCode === 9) {
              //tab

              if (refIsFocused(rightButtonRef)) {
                e.preventDefault();
                modalCloseRef.current.focus();
              }
            }
          }}
          size="large"
        >
          <Modal.Content>
            <div className="preview-image-modal">
              <Grid.Row>
                <Grid.Column>
                  <Button
                    icon
                    onClick={() => carouselModal('left')}
                    title={intl.formatMessage(messages.prevImage)}
                    className="arrow-image arrow-left"
                    ref={leftButtonRef}
                  >
                    <Icon prefix="fa" icon="chevron-left" className="icon" />
                  </Button>
                </Grid.Column>
                <Grid.Column>
                  <div className="preview-image-wrapper">
                    {previewImage && (
                      <img
                        alt={previewImageAlt}
                        role="presentation"
                        src={previewImage}
                        loading="lazy"
                      />
                    )}
                    <ListingSliderPreviewImageMore
                      item={data.items?.[previewIndex]}
                    />
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <Button
                    icon
                    onClick={() => carouselModal('right')}
                    title={intl.formatMessage(messages.nextImage)}
                    className="arrow-image arrow-right"
                    ref={rightButtonRef}
                  >
                    <Icon prefix="fa" icon="chevron-right" className="icon" />
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </div>
          </Modal.Content>
        </Modal>
      </div>
    </VariationWrapper>
  );
};

export default Slider;
